import {
  type MaasWorkload as MaasBaseWorkload,
  type MaasGroupSpec,
  type MaasGroupedWorkloadSpec,
  type MaasInstanceFormValues,
  type MaasModelSource,
  MaasModelSourceType,
  type MaasRunMode,
} from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import type { MaasInitCommand, MaasTaskTemplateStep } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import type { MaasCommonEnvFormItem } from '@/delivery-type-plugin/capabilities/maas-init/utils/commonEnv';
import type { ModelCapabilityModalityKey } from '@/types/model-capabilities';

/** 单条模型来源（API：name/size 与 http/s3/stackscope 等同级） */
export type MaasModelSourceEntry = {
  name: string;
  /** 模型大小（K8s quantity，如 10Gi） */
  size: string;
} & MaasModelSource;

/** 模型实例 spec.model 结构 */
export interface MaasModel {
  mountPath: string;
  sources: MaasModelSourceEntry[];
  /** 模型类型（能力模态，对应表单 _modelCategory） */
  types?: ModelCapabilityModalityKey[];
}

/** 推理节点拓扑（normal / primarySecondary / prefill / decode 共用） */
export interface MaasInferenceTopologySpec {
  /** 该拓扑副本数（提交 inference.*.replicas） */
  replicas?: number;
  headGroupSpec?: MaasGroupSpec;
  workerGroupSpecs?: MaasGroupSpec[];
  /** 该拓扑任务模板（提交 inference.*.taskTemplate / kvOffload.taskTemplate） */
  taskTemplate?: MaasTaskTemplateStep[];
  /** 该拓扑环境变量（提交 inference.*.environment.env / kvOffload.environment.env） */
  environment?: { env?: MaasCommonEnvFormItem[] };
}

/** 模型实例 spec.kvOffload：单套拓扑，主从以是否存在 headGroupSpec 区分 */
export type MaasKvOffloadSpec = MaasInferenceTopologySpec;

/** 模型实例 spec.inference */
export interface MaasInferenceSpec {
  /** 普通部署 */
  normal?: MaasInferenceTopologySpec;
  /** 主从部署 */
  primarySecondary?: MaasInferenceTopologySpec;
  prefillDecode?: {
    prefill?: MaasInferenceTopologySpec;
    decode?: MaasInferenceTopologySpec;
  };
}

/** 与后端 ModelInstanceSpec 对齐 */
export interface ModelInstanceSpec extends MaasGroupedWorkloadSpec {
  model?: MaasModel;
  initCommand?: MaasInitCommand;
  taskTemplate?: MaasTaskTemplateStep[];
  inference?: MaasInferenceSpec;
  /** 缓存节点；勾选模块时提交，主从看 headGroupSpec */
  kvOffload?: MaasKvOffloadSpec;
}

/** 模型实例 Workload（spec 含 model 字段） */
export interface MaasWorkload extends Omit<MaasBaseWorkload, 'spec'> {
  spec: ModelInstanceSpec;
}

/** 创建/编辑表单（共用 UI 字段见 MaasInstanceFormValues） */
export interface ModelInstanceFormValues extends MaasInstanceFormValues {
  model?: MaasModel;
  /** UI：PD 分离（提交 inference.prefillDecode） */
  _inferencePdSeparation?: boolean;
  /** PD：prefill 副本数、任务模板、部署模式与节点 JSON */
  prefillReplicas?: number;
  prefillTaskTemplate?: string | MaasTaskTemplateStep[];
  prefillRunMode?: MaasRunMode;
  prefillMasterNodes?: string;
  prefillWorkerNodes?: string;
  'prefill.master.systemDiskSize'?: number;
  'prefill.worker.systemDiskSize'?: number;
  prefillMasterSystemDiskPricing?: Record<string, unknown>;
  prefillWorkerSystemDiskPricing?: Record<string, unknown>;
  /** PD：decode 副本数、任务模板、部署模式与节点 JSON */
  decodeReplicas?: number;
  decodeTaskTemplate?: string | MaasTaskTemplateStep[];
  decodeRunMode?: MaasRunMode;
  decodeMasterNodes?: string;
  decodeWorkerNodes?: string;
  'decode.master.systemDiskSize'?: number;
  'decode.worker.systemDiskSize'?: number;
  decodeMasterSystemDiskPricing?: Record<string, unknown>;
  decodeWorkerSystemDiskPricing?: Record<string, unknown>;
  prefillCommonEnv?: MaasCommonEnvFormItem[];
  decodeCommonEnv?: MaasCommonEnvFormItem[];
  /** 缓存节点：副本数、部署模式、节点 JSON、环境变量与任务 */
  kvOffloadReplicas?: number;
  kvOffloadTaskTemplate?: string | MaasTaskTemplateStep[];
  kvOffloadRunMode?: MaasRunMode;
  kvOffloadMasterNodes?: string;
  kvOffloadWorkerNodes?: string;
  'kvOffload.master.systemDiskSize'?: number;
  'kvOffload.worker.systemDiskSize'?: number;
  kvOffloadMasterSystemDiskPricing?: Record<string, unknown>;
  kvOffloadWorkerSystemDiskPricing?: Record<string, unknown>;
  kvOffloadCommonEnv?: MaasCommonEnvFormItem[];
  /** UI：是否启用可调度网卡；关闭时保留选择但不提交 network.nics。 */
  _nicEnabled?: boolean;
  /** UI：模型类型（提交写入 spec.model.types） */
  _modelCategory?: ModelCapabilityModalityKey[];
  /** UI：模型与存储步骤校验占位（有 Data 卷时为 '1'） */
  maasStorageConfigured?: string;
  /** UI：添加新模型 / 添加已有模型（切换时清空 sources 与存储，保留 _modelCategory） */
  _modelStorageMode?: 'new' | 'existing';
  /** 实例级镜像拉取账密，提交时写入各节点组 registry */
  registry?: { server?: string; username?: string; password?: string };
}

/** types API model.source 选项与 MaasModelSourceType 映射 */
export const MODEL_SOURCE_API_MAP: Record<string, MaasModelSourceType> = {
  HTTP: MaasModelSourceType.Http,
  S3: MaasModelSourceType.S3,
  ModelScope: MaasModelSourceType.ModelScope,
  HuggingFace: MaasModelSourceType.HuggingFace,
  StackScope: MaasModelSourceType.StackScope,
  ModelPlaza: MaasModelSourceType.ModelPlaza,
};
